import { catalogApi } from '@gl/api-client';
import type { CatalogDto, CategoryDto, ItemDto, VariantDto } from '@gl/shared';
import { useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { api, errorMessage } from '@/lib/api';
import { queryKeys } from '@/lib/query';

export const catalog = catalogApi(api);

export const catalogKeys = {
  all: (localId: string) => [...queryKeys.local(localId), 'catalog'] as const,
};
const pricesKey = (localId: string) => [...queryKeys.local(localId), 'prices'] as const;

export function useCatalogQuery(localId: string) {
  return useQuery({ queryKey: catalogKeys.all(localId), queryFn: () => catalog.get(localId) });
}

export function invalidateCatalog(qc: QueryClient, localId: string) {
  return Promise.all([
    qc.invalidateQueries({ queryKey: catalogKeys.all(localId) }),
    qc.invalidateQueries({ queryKey: pricesKey(localId) }),
  ]);
}

/** Mutación del catálogo: toast de error/éxito e invalidación de catálogo y precios. */
export function useCatalogMutation<TVars, TData = unknown>(
  localId: string,
  fn: (vars: TVars) => Promise<TData>,
  opts: { success?: string | ((data: TData, vars: TVars) => string); onSuccess?: (data: TData, vars: TVars) => void } = {},
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: (data, vars) => {
      const msg = typeof opts.success === 'function' ? opts.success(data, vars) : opts.success;
      if (msg) toast.success(msg);
      opts.onSuccess?.(data, vars);
    },
    onError: (err) => toast.error(errorMessage(err)),
    onSettled: () => invalidateCatalog(qc, localId),
  });
}

// ---- Updates optimistas del cache ----

export function patchCatalog(qc: QueryClient, localId: string, fn: (c: CatalogDto) => CatalogDto) {
  qc.setQueryData<CatalogDto>(catalogKeys.all(localId), (prev) => (prev ? fn(prev) : prev));
}

export function mapItems(c: CatalogDto, fn: (i: ItemDto) => ItemDto): CatalogDto {
  return { categories: c.categories.map((cat) => ({ ...cat, items: cat.items.map(fn) })) };
}

export function useToggleItemAvailability(localId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { itemId: string; isAvailable: boolean }) =>
      catalog.setItemAvailability(localId, v.itemId, { isAvailable: v.isAvailable }),
    onMutate: async (v) => {
      await qc.cancelQueries({ queryKey: catalogKeys.all(localId) });
      patchCatalog(qc, localId, (c) => mapItems(c, (i) => (i.id === v.itemId ? { ...i, isAvailable: v.isAvailable } : i)));
    },
    onError: (err) => toast.error(errorMessage(err)),
    onSettled: () => invalidateCatalog(qc, localId),
  });
}

export function useToggleVariantAvailability(localId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { variantId: string; isAvailable: boolean }) =>
      catalog.updateVariant(localId, v.variantId, { isAvailable: v.isAvailable }),
    onMutate: async (v) => {
      await qc.cancelQueries({ queryKey: catalogKeys.all(localId) });
      patchCatalog(qc, localId, (c) =>
        mapItems(c, (i) => ({
          ...i,
          variants: i.variants.map((vr: VariantDto) => (vr.id === v.variantId ? { ...vr, isAvailable: v.isAvailable } : vr)),
        })),
      );
    },
    onError: (err) => toast.error(errorMessage(err)),
    onSettled: () => invalidateCatalog(qc, localId),
  });
}

export type ReorderVars =
  | { kind: 'categories'; ordered: CategoryDto[] }
  | { kind: 'items'; categoryId: string; ordered: ItemDto[] };

export function useReorder(localId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: ReorderVars) =>
      v.kind === 'categories'
        ? catalog.reorder(localId, { categories: v.ordered.map((c, i) => ({ id: c.id, sortOrder: i })) })
        : catalog.reorder(localId, {
            items: v.ordered.map((it, i) => ({ id: it.id, categoryId: v.categoryId, sortOrder: i })),
          }),
    onMutate: async (v) => {
      await qc.cancelQueries({ queryKey: catalogKeys.all(localId) });
      patchCatalog(qc, localId, (c) =>
        v.kind === 'categories'
          ? { categories: v.ordered.map((cat, i) => ({ ...cat, sortOrder: i })) }
          : {
              categories: c.categories.map((cat) =>
                cat.id === v.categoryId ? { ...cat, items: v.ordered.map((it, i) => ({ ...it, sortOrder: i })) } : cat,
              ),
            },
      );
    },
    onSuccess: (data) => qc.setQueryData(catalogKeys.all(localId), data),
    onError: (err) => {
      toast.error(errorMessage(err));
      void invalidateCatalog(qc, localId);
    },
    onSettled: () => qc.invalidateQueries({ queryKey: pricesKey(localId) }),
  });
}
